/**
 * The analyst portal (AIC-99): sign-in through the IDC Platform SDK shell, then a role-aware shell and nav.
 * /auth/login and /auth/callback belong to the SDK shell script; the app only shows a placeholder there.
 * No session or a 401 → sign in again; 403 from /me (no Software Tracker role) → a friendly no-access page.
 * AIC-1486: with the Notetaker flag on, a token without a Software Tracker role boots into the Workspace instead,
 * before and without any tracker-api call (notetaker/workspace.tsx).
 */
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { clearPortalReads, onPortalMutation, setPortalReadScope, withReadSignal, isAbortError, ApiError, type PortalApi } from "./api/client.ts";
import { PortalContext, type PortalContextValue } from "./api/hooks.ts";
import type { Me, NavCounts } from "./api/schemas.ts";
import { callbackFailed, SignInError, SHELL_WAIT_MS, isAuthRoute, type Auth } from "./auth/session.ts";
import { AnalystPreviewExit } from "./components/AnalystPreview.tsx";
import { deferredView, DeferredView } from "./components/DeferredView.tsx";
import { PublicPage, Shell, Standalone } from "./components/Shell.tsx";
import { notetakerEnabled } from "./notetaker/flag.ts";
import { useNotetaker } from "./notetaker/shell.tsx";
import { WORKSPACE_NAME, WorkspaceOnly, workspaceEntry, type WorkspacePerson } from "./notetaker/workspace.tsx";
import { OnboardingProvider } from "./onboarding/OnboardingProvider.tsx";
import { Tour } from "./onboarding/Tour.tsx";
import { Welcome } from "./onboarding/Welcome.tsx";
import { hasPortalRole, isAdmin, isManager } from "./roles.ts";
import { Link, match, navigate, rewriteLocation, setNavigationPreloader, useRoute, type Route } from "./router.tsx";
import { PracticeChrome } from "./practice/PracticeBanner.tsx";
import { endPractice, getPracticeState, setPracticePreview, takePracticeReturn, usePracticeState } from "./practice/practiceStore.ts";
import { warmAssignmentAssets } from "./assignmentWarmup.ts";
import { beginPortalRoute, startPortalTelemetry, stopPortalTelemetry } from "./telemetry.ts";
import { SignIn } from "./screens/SignIn.tsx";
import { preloadInsightsTab } from "./screens/insights/deferredTabs.ts";
import { Today } from "./screens/Today.tsx";
import { clearPortalStorage, pruneOtherWorkbenchStorage } from "./screens/workbench/storage.ts";

// Module-scope lazy imports keep loaded views stable across navigation.
const AssignmentPreviewPage = deferredView(() => import("./screens/AssignmentPreview.tsx").then(m => ({ default: m.AssignmentPreviewPage })));
const AnalystPreviewPage = deferredView(() => import("./screens/AnalystPreviewPage.tsx").then(m => ({ default: m.AnalystPreviewPage })));
const Workbench = deferredView(() => import("./screens/Workbench.tsx").then((m) => ({ default: m.Workbench })));
const GettingStarted = deferredView(() => import("./onboarding/GettingStarted.tsx").then((m) => ({ default: m.GettingStarted })));
const PeoplePage = deferredView(() => import("./screens/People.tsx").then((m) => ({ default: m.People })));
const MyProfile = deferredView(() => import("./screens/MyProfile.tsx").then((m) => ({ default: m.MyProfile })));
const AdminCycle = deferredView(() => import("./screens/AdminCycle.tsx").then((m) => ({ default: m.AdminCycle })));
const AdminSettings = deferredView(() => import("./screens/AdminSettings.tsx").then((m) => ({ default: m.AdminSettings })));
const AssignmentDetail = deferredView(
  () => import("./screens/AssignmentDetail.tsx").then((m) => ({ default: m.AssignmentDetail })),
  // Literal query creates a second Vite-managed entry: the first retry cannot
  // reuse a browser-cached rejection of the entry chunk during navigation.
  () => import("./screens/AssignmentDetail.tsx?retry=1").then((m) => ({ default: (m as unknown as typeof import("./screens/AssignmentDetail.tsx")).AssignmentDetail })),
);
const Assignments = deferredView(() => import("./screens/Assignments.tsx").then((m) => ({ default: m.Assignments })));
const Dashboard = deferredView(() => import("./screens/Dashboard.tsx").then((m) => ({ default: m.Dashboard })));
const Insights = deferredView(() => import("./screens/insights/Insights.tsx").then((m) => ({ default: m.Insights })));
const Reconciliation = deferredView(() => import("./screens/Reconciliation.tsx").then((m) => ({ default: m.Reconciliation })));
const ResultDetail = deferredView(() => import("./screens/ResultDetail.tsx").then((m) => ({ default: m.ResultDetail })));
const PracticeSessions = deferredView(() => import("./screens/PracticeSessions.tsx").then((m) => ({ default: m.PracticeSessions })));
const PracticeOutbox = deferredView(() => import("./screens/PracticeOutbox.tsx").then((m) => ({ default: m.PracticeOutbox })));
const Review = deferredView(() => import("./screens/Review.tsx").then((m) => ({ default: m.Review })));

type RouteEntry = { render(params: Record<string, string>, roles: readonly string[]): ReactNode; preload(params: Record<string, string>): Promise<unknown>; allowPreload?: (roles: readonly string[] | null) => boolean };
/** AIC-511: how often the rail's counts (and the bell's unread count with them) are read again while the tab is visible. */
const COUNTS_POLL_MS = 60_000;
/** AIC-557: who may open the page in words (never a role id), where access is managed, and a way back. */
const accessRefused = (title: string, who: string) => (
  <Standalone title={title}><p>Only {who} can open this page. Access is managed in the IDC portal.</p><p><Link className="ipd-link" to="/today">Back to Today</Link></p></Standalone>
);
const adminOnly = accessRefused("Admins only", "admins");
const homeRoute: RouteEntry = { render: () => <Workbench.View />, preload: () => Workbench.preload() };
const todayRoute: RouteEntry = { render: () => <Today />, preload: async () => {} };
const helpRoute: RouteEntry = { render: () => <GettingStarted.View />, preload: () => GettingStarted.preload() };
const assignmentsRoute: RouteEntry = { render: () => <Assignments.View />, preload: () => Assignments.preload() };
const assignmentRoute: RouteEntry = { render: (x) => <AssignmentDetail.View key={x.id} id={x.id!} />, preload: () => AssignmentDetail.preload() };
const assignmentPreviewRoute: RouteEntry = { render: x => <AssignmentPreviewPage.View key={x.id} id={x.id!} />, preload: () => AssignmentPreviewPage.preload() };
const analystPreviewRoute: RouteEntry = { render: (_, roles) => isAdmin(roles) ? <AnalystPreviewPage.View /> : adminOnly, preload: () => AnalystPreviewPage.preload(), allowPreload: roles => Boolean(roles && isAdmin(roles)) };
const practiceRoute: RouteEntry = { render: () => <PracticeSessions.View />, preload: () => PracticeSessions.preload() };
const practiceOutboxRoute: RouteEntry = { render: () => <PracticeOutbox.View />, preload: () => PracticeOutbox.preload() };
const reviewRoute: RouteEntry = { render: () => <Review.View />, preload: () => Review.preload() };
const resultRoute: RouteEntry = { render: (x) => <ResultDetail.View key={x.id} id={x.id!} />, preload: () => ResultDetail.preload() };
const dashboardRoute: RouteEntry = { render: () => <Dashboard.View />, preload: () => Dashboard.preload() };
const insightsRoute: RouteEntry = { render: (x) => <Insights.View tab={x.tab ?? null} />, preload: (x) => Promise.all([Insights.preload(), preloadInsightsTab(x.tab ?? null)]).then(() => undefined) };
const profileRoute: RouteEntry = { render: () => <MyProfile.View />, preload: () => MyProfile.preload() };
const analystProfileRoute: RouteEntry = { render: (x) => <MyProfile.View key={x.id} id={x.id!} />, preload: () => MyProfile.preload() };
const peopleRoute: RouteEntry = { render: (_, roles) => isAdmin(roles) ? <PeoplePage.View /> : adminOnly, preload: () => PeoplePage.preload(), allowPreload: roles => Boolean(roles && isAdmin(roles)) };
const adminCycleRoute: RouteEntry = { render: (_, roles) => isAdmin(roles) ? <AdminCycle.View /> : adminOnly, preload: () => AdminCycle.preload(), allowPreload: roles => Boolean(roles && isAdmin(roles)) };
const reconciliationRoute: RouteEntry = { render: (_, roles) => isAdmin(roles) || isManager(roles) ? <Reconciliation.View /> : accessRefused("Admins and managers only", "admins and managers"), preload: () => Reconciliation.preload(), allowPreload: roles => Boolean(roles && (isAdmin(roles) || isManager(roles))) };
const adminSettingsRoute: RouteEntry = { render: (_, roles) => isAdmin(roles) ? <AdminSettings.View /> : adminOnly, preload: () => AdminSettings.preload(), allowPreload: roles => Boolean(roles && isAdmin(roles)) };

/** The one portal route selector: rendering and preloading consume this same descriptor. */
function screenFor(path: string): { route: RouteEntry; params: Record<string, string> } | null {
  if (path === "/") return { route: homeRoute, params: {} };
  if (path === "/today") return { route: todayRoute, params: {} };
  if (path === "/help") return { route: helpRoute, params: {} };
  if (path === "/assignments") return { route: assignmentsRoute, params: {} };
  const assignmentPreview = match("/assignments/:id/preview", path); if (assignmentPreview) return { route: assignmentPreviewRoute, params: assignmentPreview };
  const assignment = match("/assignments/:id", path); if (assignment) return { route: assignmentRoute, params: assignment };
  if (path === "/profile") return { route: profileRoute, params: {} };
  const analystProfile = match("/profile/:id", path); if (analystProfile) return { route: analystProfileRoute, params: analystProfile };
  if (path === "/practice") return { route: practiceRoute, params: {} };
  if (path === "/practice/outbox") return { route: practiceOutboxRoute, params: {} };
  if (path === "/review") return { route: reviewRoute, params: {} };
  const result = match("/results/:id", path); if (result) return { route: resultRoute, params: result };
  if (path === "/dashboard") return { route: dashboardRoute, params: {} };
  if (path === "/insights") return { route: insightsRoute, params: {} };
  const insight = match("/insights/:tab", path); if (insight) return { route: insightsRoute, params: insight };
  if (path === "/admin/analyst-preview") return { route: analystPreviewRoute, params: {} };
  if (path === "/admin/people") return { route: peopleRoute, params: {} };
  if (path === "/admin/cycle" || path === "/admin/cycle/imports") return { route: adminCycleRoute, params: {} };
  if (path === "/admin/cycle/reconciliation") return { route: reconciliationRoute, params: {} };
  if (path === "/admin/settings") return { route: adminSettingsRoute, params: {} };
  return null;
}
export function preloadRoute(path: string, roles: readonly string[] | null = null): void {
  const selected = screenFor(path);
  if (selected && (!selected.route.allowPreload || selected.route.allowPreload(roles))) void selected.route.preload(selected.params).catch(() => {});
}

type Boot = { state: "loading" } | { state: "signin" } | { state: "forbidden"; email: string } | { state: "failed" | "unavailable" } | { state: "error" } | { state: "ready"; me: Me } | { state: "workspace"; person: WorkspacePerson };

/** The screens a practice session may use (AIC-463): the practice copy, Review and Results, and the practice pages. Help only explains. */
export function practiceAllows(path: string): boolean {
  return path === "/practice" || path === "/practice/outbox" || path === "/review" || path === "/help"
    || match("/assignments/:id", path) !== null || match("/results/:id", path) !== null;
}

function NotAvailableInPractice() {
  return (
    <Standalone title="Not available in practice">
      <p>This page works on real data, so it is closed while you practise. Nothing here reaches vendors, Outlook or the Data Layer.</p>
      <p><button type="button" className="ipd-btn" onClick={() => endPractice()}>Exit practice</button></p>
    </Standalone>
  );
}

export function NotFound() {
  return <Standalone title="Page not found"><p>That page doesn't exist. Use the menu above.</p></Standalone>;
}

/**
 * The route the app shows for a URL. A legacy Outlook callback, "/?outlook=…", meant Today before "/" became the Analyst
 * Workbench (AIC-404): it resolves to "/today" with its query here, before anything renders, so the Workbench never
 * renders, fetches its queue or records a "/" telemetry route for it.
 */
export function resolveRoute(route: Route): Route {
  // AIC-556: Admin → Analysts became Admin → People; the old address (a bookmark, an old link) goes to it, query and all.
  if (route.path === "/admin") return { path: "/admin/people", query: route.query };
  return route.path === "/" && route.query.has("outlook") ? { path: "/today", query: route.query } : route;
}

export function App({ auth, api }: { auth: Auth; api: PortalApi }) {
  const live = useRoute();
  const route = resolveRoute(live);
  const redirected = route !== live ? `${route.path}${route.query.size ? `?${route.query}` : ""}${window.location.hash}` : null;
  // The address bar follows the resolved route before paint, as the same history entry (no navigation of its own).
  useLayoutEffect(() => { if (redirected) rewriteLocation(redirected); }, [redirected]);
  const previousRoute = useRef<string | null>(null);
  const [previewInvalid, setPreviewInvalid] = useState(false);
  useEffect(() => {
    const invalid = () => { clearPortalReads(api); setPreviewInvalid(true); };
    const changed = (e: StorageEvent) => { if (e.key === "swt-preview-changed") { clearPortalReads(api); window.location.reload(); } };
    window.addEventListener("swt-preview-invalid", invalid);
    window.addEventListener("storage", changed);
    return () => { window.removeEventListener("swt-preview-invalid", invalid); window.removeEventListener("storage", changed); };
  }, [api]);
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  const expire = useCallback(() => { clearPortalReads(api); setBoot({ state: "signin" }); }, [api]);
  const signOut = () => { clearPortalReads(api); clearPortalStorage(); auth.signOut(); };

  useEffect(() => {
    if (isAuthRoute(route.path)) return;
    preloadRoute(route.path);
    let live = true;
    const controller = new AbortController();
    const software = () => withReadSignal(api, controller.signal).me().then(
      (me) => {
        if (!live) return;
        setPortalReadScope(api, JSON.stringify([me.email, me.roles, me.build_mode, me.preview ?? null]));
        setBoot(hasPortalRole(me.roles) ? { state: "ready", me } : { state: "forbidden", email: me.email });
      },
      (e: unknown) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 401) setBoot({ state: "signin" });
        else if (e instanceof ApiError && e.status === 403) setBoot({ state: "forbidden", email: "" });
        else if (e instanceof SignInError) setBoot({ state: e.reason });
        else setBoot({ state: "error" });
      },
    );
    // AIC-1486: flag off, no token read; with it on, only a token without a Software Tracker role skips /me.
    if (!notetakerEnabled()) void software();
    else void workspaceEntry(auth).then((person) => { if (!live) return; if (person) setBoot({ state: "workspace", person }); else void software(); });
    return () => { live = false; controller.abort(); };
    // Once per page load: route changes inside the app don't re-check /me.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const me = boot.state === "ready" ? boot.me : null;
  // AIC-463: the active practice session. Everything under it is keyed by its id, so a screen never carries real data
  // into practice or practice data out of it; requests carry its id (api/client.ts) and the read cache is keyed by it.
  const practiceState = usePracticeState();
  const practice = me && !me.preview ? practiceState.session : null;
  // A preview reads real data only: no request carries the practice header while it is on.
  useLayoutEffect(() => { setPracticePreview(Boolean(me?.preview)); }, [me]);
  useEffect(() => {
    // Navigate first, then release the hold below, so the screen that follows is the real one and the practice copy's
    // page never mounts again in real scope.
    const to = getPracticeState().returnTo;
    if (to === null) return;
    navigate(to, { replace: true });
    takePracticeReturn();
  }, [practiceState.returnTo]);
  useEffect(() => {
    if (!me || previewInvalid || me.preview) { stopPortalTelemetry(); previousRoute.current = null; }
  }, [me, previewInvalid]);
  useLayoutEffect(() => {
    if (!me || previewInvalid || me.preview || !hasPortalRole(me.roles)) return;
    // The initial document starts at the Performance timeline origin; later
    // router transitions are the sole SPA samples.
    if (previousRoute.current === null) { startPortalTelemetry(auth); beginPortalRoute(route.path, false, 0); }
    previousRoute.current = route.path;
  }, [auth, me, previewInvalid]);
  useEffect(() => () => stopPortalTelemetry(), []);
  // AIC-404 backup to the sign-out clear: once the signed-in user is known, drop any other identity's Workbench keys.
  // Never in preview: a preview reads and writes no Workbench storage, the operator's own included.
  useEffect(() => { if (me && !me.preview) pruneOtherWorkbenchStorage(me.email); }, [me]);

  useEffect(() => { if (me) preloadRoute(route.path, me.roles); }, [me, route.path]);
  useEffect(() => {
    if (!me) return setNavigationPreloader(null);
    let prefetch: AbortController | null = null;
    setNavigationPreloader((to) => {
      prefetch?.abort();
      const assignment = match("/assignments/:id", new URL(to, window.location.href).pathname);
      // ReadPool coalesces this user-initiated request with AssignmentDetail's
      // own read. It never runs for startup/hover prefetching.
      if (assignment) {
        prefetch = new AbortController();
        void withReadSignal(api, prefetch.signal).prefetchAssignment(assignment.id!).catch(() => {});
      }
    });
    return () => { prefetch?.abort(); setNavigationPreloader(null); };
  }, [api, me]);
  // After authenticated Today commits, warm its primary destination without fetching assignment data.
  // Once started, the warm-up outlives Today: leaving Today is usually the click it exists for, and aborting
  // then would make that click download the chunk from scratch. Only a new identity (or unmount) cancels it.
  const warmup = useRef<{ me: Me; controller: AbortController } | null>(null);
  useEffect(() => {
    if (!me || !hasPortalRole(me.roles) || (route.path !== "/" && route.path !== "/today") || warmup.current?.me === me) return;
    const timer = setTimeout(() => {
      const controller = new AbortController();
      warmup.current = { me, controller };
      warmAssignmentAssets(controller.signal);
    }, 0);
    return () => clearTimeout(timer);
  }, [me, route.path]);
  useEffect(() => () => { warmup.current?.controller.abort(); warmup.current = null; }, [me]);
  useEffect(() => {
    if (!me?.preview) return;
    const timer = setTimeout(() => { clearPortalReads(api); setPreviewInvalid(true); }, Math.max(0, Date.parse(me.preview.expires_at) - Date.now()));
    return () => clearTimeout(timer);
  }, [api, me]);
  // Counts reuse up to 10 seconds on navigation; a completed mutation also refreshes the current rail.
  const [mutation, setMutation] = useState(0);
  useEffect(() => onPortalMutation(api, () => setMutation((n) => n + 1)), [api]);
  // "failed": the rail's Review badge reads "—" (prototype), never a false zero.
  const [counts, setCounts] = useState<NavCounts | "failed" | null>(null);
  // AIC-511: the same counts are read again every COUNTS_POLL_MS while the tab is visible (and on coming back to it), so
  // the bell's unread count moves without a navigation. One light read; no other polling endpoint.
  const [poll, setPoll] = useState(0);
  useEffect(() => {
    if (!me || previewInvalid || typeof document === "undefined") return;
    const tick = () => { if (document.visibilityState === "visible") setPoll((n) => n + 1); };
    const timer = setInterval(tick, COUNTS_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", tick); };
  }, [me, previewInvalid]);
  useEffect(() => {
    if (!me || previewInvalid) return;
    let live = true;
    const controller = new AbortController();
    withReadSignal(api, controller.signal).navCounts().then((c) => { if (live) setCounts(c); }, (error: unknown) => { if (live && !isAbortError(error)) setCounts("failed"); });
    return () => { live = false; controller.abort(); };
  }, [api, me, route.path, mutation, previewInvalid, poll]);
  // AIC-1437: IDC Notetaker, behind its flag (off: nothing here runs). Real sessions only, never preview or practice.
  const notetaker = useNotetaker(auth, Boolean(me && !me.preview && !practice) || boot.state === "workspace", route.path);
  // A session that ends mid-way keeps showing the mode it had; before any /me, BUILD (the safe default).
  const lastMode = useRef<"build" | "live">("build");
  if (me) lastMode.current = me.build_mode === false ? "live" : "build";
  const ctx = useMemo<PortalContextValue | null>(() => (me ? { api, roles: me.roles, email: me.email, name: me.name, buildMode: me.build_mode !== false, expire, preview: Boolean(me.preview), previewScope: me.preview ? JSON.stringify([me.preview.actor, me.preview.analyst_id]) : undefined, practice } : null), [api, me, expire, practice]);

  // Before /me answers, the mode isn't known: pre-sign-in pages show BUILD, the safe default (AIC-112).
  if (previewInvalid) return <PublicPage mode={lastMode.current} title="Analyst preview ended"><p>Preview expired or the analyst mapping changed. Exit to restore your own workspace.</p><AnalystPreviewExit onExit={() => api.stopPreview().then(() => undefined)}/></PublicPage>;
  if (isAuthRoute(route.path)) return <AuthHandoff auth={auth} failed={callbackFailed(route.path, route.query, window.location.hash)} />;
  // AIC-1487: with the Notetaker flag on, nobody is told they are in the Software Tracker until /me says so.
  if (boot.state === "loading") return notetakerEnabled()
    ? <PublicPage mode="build" title={WORKSPACE_NAME} workspaceName={WORKSPACE_NAME}><p role="status">Loading…</p></PublicPage>
    : <PublicPage mode="build" title="Software Tracker"><p role="status">Loading…</p></PublicPage>;
  if (boot.state === "signin") return <SignIn auth={auth} mode={lastMode.current} />;
  if (boot.state === "forbidden") return <SignIn auth={auth} mode={lastMode.current} state="denied" email={boot.email} />;
  if (boot.state === "failed" || boot.state === "unavailable") return <SignIn auth={auth} mode={lastMode.current} state={boot.state} />;
  if (boot.state === "workspace") return <WorkspaceOnly person={boot.person} path={route.path} notetaker={notetaker} onSignOut={signOut} />;
  if (boot.state === "error" || !ctx || !me) {
    return <PublicPage mode="build" title="Software Tracker is unavailable"><p>The portal couldn't reach its service. Try again in a minute.</p></PublicPage>;
  }
  return (
    <PortalContext.Provider value={ctx}>
      {/* AIC-302: keyed on the signed-in user, so another account never sees this one's progress held in memory. */}
      <OnboardingProvider key={`${me.email}|${me.preview ? "preview" : "own"}`}>
        <Shell
          path={route.path} name={me.name} email={me.email} roles={me.roles} buildMode={me.build_mode !== false} onSignOut={signOut}
          buildTester={me.build_tester?.name ?? null} practice={practice} module={notetaker.module} entry={notetaker.entry}
          // The prototype's one rail badge: Review = open review items (results awaiting a decision are on its page).
          counts={practice ? null : counts === "failed" ? { review: null } : counts?.mapped ? { review: counts.review } : null}
          // AIC-511: the bell, the signed-in person's own: not in analyst preview or practice; null hides it.
          notifications={practice || me.preview || counts === null || counts === "failed" ? null : counts.notifications ?? null}
          countsRead={counts}
          notice={<>{me.preview ? <section className="ipd-notice analyst-preview-band" role="region" aria-label="Read-only analyst preview"><div><strong>Viewing {me.name} · {me.email} · Read-only analyst workspace</strong><p>Signed in as {me.preview.actor}. Preview ends at {new Date(me.preview.expires_at).toLocaleTimeString()}. No approvals, edits or Outlook actions.</p><AnalystPreviewExit onExit={() => api.stopPreview().then(() => undefined)}/></div></section> : null}<PracticeChrome /></>}
        >
          <Fragment key={practice?.id ?? "real"}>
            {practiceState.returnTo !== null ? null : (
              <DeferredView resetKey={route.path}>{(() => {
                const selected = screenFor(route.path);
                if (me.preview && route.path.startsWith("/practice")) return <Standalone title="Not available in analyst preview"><p>Practice works on a copy and writes to it. Leave analyst preview to practise.</p></Standalone>;
                if (practice && selected && !practiceAllows(route.path)) return <NotAvailableInPractice />;
                if (!selected && notetaker.view) return notetaker.view;
                return selected ? selected.route.render(selected.params, me.roles) : <NotFound />;
              })()}</DeferredView>
            )}
          </Fragment>
          <Welcome path={route.path} />
          <Tour />
        </Shell>
      </OnboardingProvider>
    </PortalContext.Provider>
  );
}

/**
 * The SDK owns these routes; bound its placeholder so a missing shell cannot strand the user (AIC-344). The shell
 * defines no window.idcShell here (it only redirects), so readiness is public/shell-load-status.js's record:
 *   script failed to load (503, blocked)            → unavailable at once
 *   script not seen loading within SHELL_WAIT_MS    → unavailable (missing, or still hanging)
 *   loaded, but no redirect within HANDOFF_WAIT_MS  → failed
 * Neither page redirects by itself; Retry reloads this page, so there is no loop.
 */
const HANDOFF_WAIT_MS = 30_000;
function AuthHandoff({ auth, failed }: { auth: Auth; failed: boolean }) {
  const [failure, setFailure] = useState<"failed" | "unavailable" | null>(null);
  useEffect(() => {
    if (failed) return;
    if (window.idcShellLoadFailed) { setFailure("unavailable"); return; }
    let active = true;
    const missing = setTimeout(() => { if (active && (window.idcShellLoadFailed || !window.idcShellLoaded)) setFailure("unavailable"); }, SHELL_WAIT_MS);
    const stalled = setTimeout(() => { if (active) setFailure((f) => f ?? (window.idcShellLoadFailed ? "unavailable" : "failed")); }, HANDOFF_WAIT_MS);
    return () => { active = false; clearTimeout(missing); clearTimeout(stalled); };
  }, [auth, failed]);
  if (failed || failure) return <SignIn auth={auth} state={failure ?? "failed"} />;
  return <PublicPage mode="build" title="Signing you in…"><p>One moment.</p></PublicPage>;
}
